import { Fragment, type ReactNode } from 'react'

/**
 * Safe Markdown renderer.
 *
 * Renders a practical, safe *subset* of Markdown by building React elements
 * directly — it never uses `dangerouslySetInnerHTML`, so raw HTML in the source
 * is shown as text and can never execute. Link URLs are restricted to safe
 * schemes, blocking `javascript:` and similar. Supported: headings, paragraphs,
 * unordered/ordered lists, blockquotes, fenced code, and inline emphasis, code,
 * and links.
 */
export function SafeMarkdown({ markdown }: { markdown: string }) {
  return <>{renderBlocks(markdown)}</>
}

function renderBlocks(markdown: string): ReactNode[] {
  const lines = markdown.split(/\r?\n/)
  const out: ReactNode[] = []
  let key = 0
  const nextKey = () => `b${key++}`

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const trimmed = line.trim()

    if (trimmed === '') continue

    // Fenced code block.
    if (trimmed.startsWith('```')) {
      const code: string[] = []
      let j = i + 1
      while (j < lines.length && !lines[j].trim().startsWith('```')) {
        code.push(lines[j])
        j++
      }
      out.push(
        <pre key={nextKey()}>
          <code>{code.join('\n')}</code>
        </pre>,
      )
      i = j
      continue
    }

    // Heading.
    const heading = /^(#{1,6})\s+(.*)$/.exec(trimmed)
    if (heading) {
      const level = heading[1].length
      const Tag = `h${level}` as 'h1'
      out.push(<Tag key={nextKey()}>{renderInline(heading[2])}</Tag>)
      continue
    }

    // Table (GFM): a header row followed by a `---` separator row.
    if (trimmed.includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      const header = splitRow(trimmed)
      const rows: string[][] = []
      let j = i + 2
      while (j < lines.length && lines[j].trim() !== '' && lines[j].includes('|')) {
        rows.push(splitRow(lines[j]))
        j++
      }
      out.push(
        <table key={nextKey()}>
          <thead>
            <tr>
              {header.map((cell, index) => (
                <th key={index}>{renderInline(cell)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, index) => (
                  <td key={index}>{renderInline(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>,
      )
      i = j - 1
      continue
    }

    // Blockquote (consecutive `>` lines).
    if (trimmed.startsWith('>')) {
      const quote: string[] = []
      let j = i
      while (j < lines.length && lines[j].trim().startsWith('>')) {
        quote.push(lines[j].trim().replace(/^>\s?/, ''))
        j++
      }
      out.push(<blockquote key={nextKey()}>{renderInline(quote.join(' '))}</blockquote>)
      i = j - 1
      continue
    }

    // Unordered list.
    if (/^[-*]\s+/.test(trimmed)) {
      const items: string[] = []
      let j = i
      while (j < lines.length && /^[-*]\s+/.test(lines[j].trim())) {
        items.push(lines[j].trim().replace(/^[-*]\s+/, ''))
        j++
      }
      out.push(
        <ul key={nextKey()}>
          {items.map((item, index) => (
            <li key={index}>{renderInline(item)}</li>
          ))}
        </ul>,
      )
      i = j - 1
      continue
    }

    // Ordered list.
    if (/^\d+\.\s+/.test(trimmed)) {
      const items: string[] = []
      let j = i
      while (j < lines.length && /^\d+\.\s+/.test(lines[j].trim())) {
        items.push(lines[j].trim().replace(/^\d+\.\s+/, ''))
        j++
      }
      out.push(
        <ol key={nextKey()}>
          {items.map((item, index) => (
            <li key={index}>{renderInline(item)}</li>
          ))}
        </ol>,
      )
      i = j - 1
      continue
    }

    // Paragraph (consecutive plain lines).
    const para: string[] = []
    let j = i
    while (j < lines.length && lines[j].trim() !== '' && !isBlockStart(lines[j].trim())) {
      para.push(lines[j].trim())
      j++
    }
    out.push(<p key={nextKey()}>{renderInline(para.join(' '))}</p>)
    i = j - 1
  }

  return out
}

function isBlockStart(trimmed: string): boolean {
  return (
    trimmed.startsWith('```') ||
    /^(#{1,6})\s+/.test(trimmed) ||
    trimmed.startsWith('>') ||
    /^[-*]\s+/.test(trimmed) ||
    /^\d+\.\s+/.test(trimmed)
  )
}

// Inline: `code`, [text](url), **bold**, *italic* / _italic_.
const INLINE = /(`[^`]+`)|(\[[^\]]+\]\([^)\s]+\))|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(_[^_]+_)/

function renderInline(text: string): ReactNode {
  const nodes: ReactNode[] = []
  let rest = text
  let key = 0

  while (rest.length > 0) {
    const match = INLINE.exec(rest)
    if (!match || match.index === undefined) {
      nodes.push(<Fragment key={key++}>{rest}</Fragment>)
      break
    }

    if (match.index > 0) {
      nodes.push(<Fragment key={key++}>{rest.slice(0, match.index)}</Fragment>)
    }

    const token = match[0]
    if (token.startsWith('`')) {
      nodes.push(<code key={key++}>{token.slice(1, -1)}</code>)
    } else if (token.startsWith('[')) {
      const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(token)
      if (link && isSafeUrl(link[2])) {
        nodes.push(
          <a key={key++} href={link[2]} rel="noopener noreferrer">
            {renderInline(link[1])}
          </a>,
        )
      } else {
        // Unsafe or malformed link: render as plain text, never as a live href.
        nodes.push(<Fragment key={key++}>{token}</Fragment>)
      }
    } else if (token.startsWith('**')) {
      nodes.push(<strong key={key++}>{renderInline(token.slice(2, -2))}</strong>)
    } else {
      nodes.push(<em key={key++}>{renderInline(token.slice(1, -1))}</em>)
    }

    rest = rest.slice(match.index + token.length)
  }

  return nodes
}

function isTableSeparator(line: string): boolean {
  const trimmed = line.trim()
  if (!trimmed.includes('|') && !trimmed.includes('-')) return false
  const cells = splitRow(trimmed)
  return cells.length > 0 && cells.every((cell) => /^:?-+:?$/.test(cell.trim()))
}

function splitRow(line: string): string[] {
  let s = line.trim()
  if (s.startsWith('|')) s = s.slice(1)
  if (s.endsWith('|')) s = s.slice(0, -1)
  return s.split('|').map((cell) => cell.trim())
}

function isSafeUrl(url: string): boolean {
  // Allow relative URLs and explicit http(s)/mailto; reject javascript:, data:, etc.
  if (/^(https?:|mailto:)/i.test(url)) return true
  return /^[./#?]/.test(url) || !/^[a-z][a-z0-9+.-]*:/i.test(url)
}
